import { FC, useRef, useState } from "react";
import { useFormik } from "formik";
import { toFormikValidate } from "zod-formik-adapter";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/UI/Dialog/Dialog";
import { Button } from "@/components/UI/Buttons/Button";
import profileDocumentService from "@/services/profileDocumentService";
import { Upload } from "lucide-react";

interface UploadDocumentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: () => void;
}

const schema = z.object({
  type: z.string().min(1, "Document type is required"),
});

export const UploadDocumentModal: FC<UploadDocumentModalProps> = ({
  isOpen,
  onOpenChange,
  onUploaded,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formik = useFormik({
    initialValues: { type: "" },
    validate: toFormikValidate(schema),
    onSubmit: async (values) => {
      setErrorMessage(undefined);
      setFileError(undefined);

      if (!file) {
        setFileError("Please select a file to upload");
        return;
      }

      try {
        await profileDocumentService.uploadDocument(values.type.trim(), file);
        resetForm();
        onUploaded();
        onOpenChange(false);
      } catch (error) {
        console.error("Upload failed:", error);
        setErrorMessage(`Failed to upload document: ${error}`);
      }
    },
  });

  const resetForm = () => {
    formik.resetForm();
    setFile(null);
    setFileError(undefined);
    setErrorMessage(undefined);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (open: boolean) => {
    if (!open) resetForm();
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-auto max-w-screen bg-white">
        <div className="w-[80vw] max-w-md p-3 md:p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-ink text-xl font-bold">
              Upload Document
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Select a document type and file to upload
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                Document Type
              </label>
              <input
                type="text"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="e.g. Blood, Genome"
                className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#6935B1] focus:ring-1 focus:ring-[#6935B1]"
              />
              {formik.touched.type && formik.errors.type && (
                <span className="text-xs text-red-500">
                  {formik.errors.type}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">File</label>
              <div
                className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 transition-colors hover:border-[#6935B1]"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-500">
                  {file ? file.name : "Click to select a file"}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setFileError(undefined);
                  }}
                />
              </div>
              {fileError && (
                <span className="text-xs text-red-500">{fileError}</span>
              )}
            </div>

            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-500">
                {errorMessage}
              </div>
            )}

            <div className="mt-2 flex gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleClose(false)}
                disabled={formik.isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={formik.isSubmitting}
                className="bg-primary hover:bg-primary/90 flex-1"
              >
                {formik.isSubmitting ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
